import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/shared/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Pagination } from '@/shared/ui/pagination';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { useAiModels } from '@/features/ai-credits/api/use-ai-admin';
import { AiSettingsForm } from '@/features/ai-credits/ui/ai-settings-form';
import { formatUsd, modelLabel } from '@/features/ai-credits/model/format';
import type { AiSettingSource } from '@/features/ai-credits/model/types';
import {
  usePassbookStores,
  useUpdateStore,
  type PassbookPermissionRow,
  type StorePermissionRow,
} from '../api/use-permissions';

const PAGE_SIZE = 15;

function hasOverrides(store: StorePermissionRow): boolean {
  return Object.values(store.aiSettings).some((value) => value !== null);
}

/** Valor em vigor: forte quando é da loja, apagado quando vem de cima. */
function Effective({ source, children }: { source: AiSettingSource; children: React.ReactNode }) {
  return <span className={source === 'store' ? 'font-medium' : 'text-muted-foreground'}>{children}</span>;
}

/** Exceções por loja: cada uma pode sobrescrever o padrão de IA do passbook. */
export function StoresPanel({ passbook }: { passbook: PassbookPermissionRow }) {
  const { data: catalog } = useAiModels();
  const update = useUpdateStore();
  const [editing, setEditing] = useState<StorePermissionRow | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = usePassbookStores(passbook.id, { page, limit: PAGE_SIZE, search: debouncedSearch });
  const rows = data?.rows ?? [];

  return (
    <>
      <section className="overflow-hidden rounded-2xl border border-border/60 bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Exceções por loja</h3>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Loja, código ou cidade"
              className="h-9 pl-9"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Loja</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead className="text-right">Limite</TableHead>
                <TableHead className="hidden sm:table-cell">Renovação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={4}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                    {debouncedSearch ? 'Nenhuma loja com esse termo.' : 'Sem lojas: vale o padrão do passbook.'}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((store) => {
                  const { values, sources } = store.effective;
                  return (
                    <TableRow key={store.id} className="cursor-pointer" onClick={() => setEditing(store)} tabIndex={0}>
                      <TableCell>
                        <div className="flex min-w-0 items-center gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{store.name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {store.storeCode}
                              {store.city ? ` · ${store.city}` : ''}
                            </p>
                          </div>
                          {hasOverrides(store) ? (
                            <Badge variant="accent" className="shrink-0 font-normal">
                              própria
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <Effective source={sources.model}>{modelLabel(values.model, catalog?.models)}</Effective>
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        <Effective source={sources.monthlyLimitUsd}>{formatUsd(values.monthlyLimitUsd)}</Effective>
                      </TableCell>
                      <TableCell className="hidden text-sm sm:table-cell">
                        <Effective source={sources.renewMonthly}>{values.renewMonthly ? 'Todo mês' : 'Não renova'}</Effective>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        {(data?.total ?? 0) > PAGE_SIZE ? (
          <div className="border-t border-border/60 px-3 py-2">
            <Pagination page={page} pageSize={PAGE_SIZE} total={data?.total ?? 0} onPageChange={setPage} />
          </div>
        ) : null}
      </section>

      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          {editing ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {editing.name}
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    {editing.storeCode}
                    {editing.city ? ` · ${editing.city}` : ''}
                  </span>
                </DialogTitle>
              </DialogHeader>
              <AiSettingsForm
                level="store"
                value={editing.aiSettings}
                inherited={editing.effective}
                saving={update.isPending}
                onSave={(aiSettings) =>
                  update.mutateAsync(
                    { id: editing.id, aiSettings },
                    {
                      onSuccess: (row) => {
                        setEditing(row);
                        toast.success('IA da loja salva.');
                      },
                      onError: () => toast.error('Não deu para salvar a loja.'),
                    },
                  )
                }
              />
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
